import { Content, Localized, XcoreCore, XcoreConfig } from "@robe/core";
import { XcoreProps } from "../../getXcoreProps/types";
import { XcoreDataUtils, xcoreDataUtils } from "../../hooks/useXcoreUtils/utils";
import { createContext, PropsWithChildren, useMemo } from "react";
import { RequestContextProvider } from "../RequestContext";

export type XcoreData<
  PageContent extends Content = Content,
  ContentSingletons extends Content[] = Content[],
  MultipleContents extends Content[] = Content[],
  Data = {},
> = Omit<XcoreProps<PageContent, ContentSingletons, MultipleContents, Data>, "config" | "ctx"> &
  XcoreDataUtils & {
    /** Xcore config */
    config: XcoreConfig;

    /** Xcore Core */
    core: XcoreCore;

    /** Resolved domains from the config */
    resolvedDomains: Record<string, string>;

    /** Base title to be displayed in every page title */
    baseTitle?: string | Localized<string>;
  };

export const XcoreDataContext = createContext<XcoreData>(null!);

interface XcoreClientProviderProps extends XcoreProps<Content, Content[], Content[], {}> {
  /** Base title to be displayed in every page title */
  baseTitle?: string | Localized<string>;

  currentLocale: string;
}

export const XcoreClientProvider = ({ children, ...props }: PropsWithChildren<XcoreClientProviderProps>) => {
  const context = useMemo(() => {
    const config = new XcoreConfig(props.config);
    const core = new XcoreCore(props.ctx);
    const resolvedDomains = config.getResolvedDomains();

    return {
      ...props,
      ...xcoreDataUtils(core),
      core,
      config,
      resolvedDomains,
    };
  }, [props]);

  return (
    <XcoreDataContext.Provider value={context}>
      <RequestContextProvider value={props.ctx}>{children}</RequestContextProvider>
    </XcoreDataContext.Provider>
  );
};
